import { Locale } from './locale';
import { LocaleSettings } from './locale-settings';

/**
 * A region identifies a version of the website.
 *
 * Each country selected by user in the `HelloWorld` is associated to a single region.
 * The region is part of the `vc_ck` cache key and is also used to fetch regional content
 * to be displayed to user.
 *
 * IMPORTANT NOTICE:
 *
 * The keys of enum are used as the region code and are sent to tracking and analytics
 * products. So DO NOT change keys without being sure of what you're doing.
 * With great power comes great responsibility.
 */
export enum Region {
  FR = 1,
  UK = 4,
  WW = 5, // Worldwide
  US = 6,
  DE = 10,
  IT = 11,
  ES = 12,
  RE = 13, // Rest of Europe
  FI = 14,
  SE = 15,
  DK = 16,
  AU = 17,
  HK = 18,
  SG = 19
}

export interface Localized {
  /**
   * Returns the locale.
   *
   * @returns the locale object
   */
  getLocale(): Locale;

  /**
   * Returns the region.
   *
   * Region identifies the website regional version.
   *
   * Example:
   *  - Region.US the US version
   *  - Region.WW the default worldwide version.
   *
   * @returns the region id
   */
  getRegion(): Region;

  /**
   * Returns the region code.
   *
   * Example:
   *  - `FR` France
   *  - `WW` worldwide version
   *  - `RE` rest of Europe.
   *
   * @returns the region code
   */
  getRegionCode(): string;

  /**
   * Returns the currency.
   *
   * Currency should be an uppercase ISO 4217 3-letter code.
   *
   * @returns the three-letter currency code
   */
  getCurrency(): string;

  /**
   * Returns the country.
   *
   * Country should be an uppercase ISO 3166 2-letter code.
   *
   * @returns the two-letter country code
   */
  getCountry(): string;

  /**
   * Returns the language.
   *
   * Language should be a lowercase ISO 639 2-letter code.
   *
   * @returns the two-letter language code
   */
  getLanguage(): string;
}

export abstract class LocalizationStrategy {
  abstract localize(): LocaleSettings;
}

const TRANSLATION_KEY_MAPPING: Map<string, string> = new Map([
  ['de', 'de-DE'],
  ['en', 'en-GB'],
  ['es', 'es-ES'],
  ['fr', 'fr-FR'],
  ['it', 'it-IT'],
  ['us', 'en-US']
]);

/**
 * Get locale Id from translation key.
 * @param key the translation key
 * @returns the locale Id (eg. en-US for English, United States).
 */
export function mapTransKeyToLocale(key: string): string {
  return TRANSLATION_KEY_MAPPING.get(key);
}

/**
 * Get translation key from locale.
 * @param locale
 * @returns the translation key
 */
export function mapLocaleToTransKey(locale: Locale): string {
  let key = locale.getLanguage();

  if (locale.getRegion() === 'US') {
    key = 'us';
  }

  return key;
}
